import { Head, router, usePage } from "@inertiajs/react"
import { AuthShell } from "@/components/AuthShell"
import { Button } from "@/components/ui/button"

import type { PageProps } from "@/types/inertia"

/** One seeded development account offered by the sign-in picker. */
type DevAccount = {
  key: string
  name: string
  organization: string | null
  description: string
}

type LoginProps = {
  csrf_token: string
  duo_configured: boolean
  /** Empty unless DEV_SIGN_IN is set in development. See DevSignIn. */
  dev_accounts: DevAccount[]
  dev_sign_in_notice: string | null
}

export default function Login() {
  const { props } = usePage<PageProps<LoginProps>>()
  const baseError = props.errors?.base
  const devAccounts = props.dev_accounts ?? []

  return (
    <>
      <Head title="Sign in">
        <meta
          name="description"
          content="Sign in to IUPAT Portal with your IUPAT Duo account to reach the staff tools available to you."
        />
        <meta property="og:title" content="Sign in" />
        <meta
          property="og:description"
          content="Sign in to IUPAT Portal with your IUPAT Duo account to reach the staff tools available to you."
        />
      </Head>
      <AuthShell>
        <div className="text-center">
          <h2>IUPAT Portal</h2>
          <p className="mt-2 text-sm text-ink-muted">
            Sign in with your IUPAT account to reach the staff tools available
            to you.
          </p>
        </div>

        {props.flash?.notice && (
          <p className="mt-4 text-center text-sm text-accent">{props.flash.notice}</p>
        )}
        {baseError && (
          <div className="callout callout-danger mt-6">
            <p className="text-sm text-danger-display">{baseError}</p>
          </div>
        )}

        {/*
          A real form POST, not an Inertia request: the OmniAuth request phase
          needs a full-page POST carrying the CSRF token.
        */}
        <form method="post" action="/auth/duo" className="mt-6">
          <input type="hidden" name="authenticity_token" value={props.csrf_token} />
          <Button type="submit" size="lg" className="w-full" disabled={!props.duo_configured}>
            Sign in with Duo
          </Button>
        </form>

        {!props.duo_configured && (
          <p className="mt-4 text-center text-xs text-ink-muted">
            Duo isn&apos;t configured on this server yet.
          </p>
        )}

        {/*
          Development only, and only with DEV_SIGN_IN set — the server sends an
          empty list otherwise, so this whole block is absent on every deployed
          Portal. See app/models/dev_sign_in.rb.
        */}
        {(devAccounts.length > 0 || props.dev_sign_in_notice) && (
          <div className="mt-8 border-t border-hairline pt-6">
            <p className="text-center text-xs uppercase tracking-[0.16em] text-ink-muted">
              Development sign-in
            </p>

            {props.dev_sign_in_notice && (
              <p className="mt-3 text-center text-xs text-ink-muted">
                {props.dev_sign_in_notice}
              </p>
            )}

            <div className="mt-4 space-y-2">
              {devAccounts.map((account) => (
                <Button
                  key={account.key}
                  type="button"
                  variant="secondary"
                  className="h-auto w-full flex-col items-start gap-0.5 whitespace-normal py-3 text-left"
                  onClick={() => router.post("/dev/sign_in", { key: account.key })}
                >
                  <span>
                    {account.name}
                    {account.organization ? ` — ${account.organization}` : ""}
                  </span>
                  <span className="text-xs font-normal text-ink-muted">
                    {account.description}
                  </span>
                </Button>
              ))}
            </div>

            <p className="mt-4 text-center text-xs text-ink-muted">
              Seeded, fake accounts. Nothing here reaches a real person&apos;s account.
            </p>
          </div>
        )}
      </AuthShell>
    </>
  )
}
